/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Quick Schedule Stepper
 * Numbered step indicator — not a dialog; renders step circles only.
 *
 * @file QuickScheduleStepper.jsx
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import React from 'react';
import PropTypes from 'prop-types';
import Icon from '../icons/Icon';
import { QuickScheduleStepperStylesRoot, classes } from './styles/QuickScheduleStepperStyles';
import { isQuickScheduleStepReachable } from './quickScheduleSteps';

const QuickScheduleStepper = ({
  steps,
  activeStepId,
  completedStepIds,
  onStepChange
}) => {
  if (!steps || steps.length <= 1) {
    return null;
  }

  const isStepCompleted = (stepId) => completedStepIds.includes(stepId);

  const isStepReachable = (stepId) => (
    isQuickScheduleStepReachable(stepId, steps, completedStepIds)
  );

  const getStepClassName = (stepId) => {
    const classNames = [classes.stepButton];

    if (stepId === activeStepId) {
      classNames.push(classes.stepActive);
    } else if (isStepCompleted(stepId)) {
      classNames.push(classes.stepCompleted);
    } else if (!isStepReachable(stepId)) {
      classNames.push(classes.stepInactive, classes.stepDisabled);
    } else {
      classNames.push(classes.stepInactive);
    }

    return classNames.join(' ');
  };

  const handleStepClick = (stepId) => {
    if (stepId === activeStepId || !onStepChange || !isStepReachable(stepId)) {
      return;
    }

    onStepChange(stepId);
  };

  const renderStepContent = (step, index) => {
    const completed = isStepCompleted(step.id);
    const isActive = step.id === activeStepId;

    if (completed && !isActive) {
      return (
        <span className={classes.tickIcon} aria-hidden="true">
          <Icon name="TickIcon" />
        </span>
      );
    }

    return step.label ?? index + 1;
  };

  return (
    <QuickScheduleStepperStylesRoot className={classes.root} aria-label="Progress">
      {steps.map((step, index) => {
        const isDisabled = step.id === activeStepId || !isStepReachable(step.id);

        return (
          <React.Fragment key={step.id}>
            <button
              type="button"
              className={getStepClassName(step.id)}
              onClick={() => handleStepClick(step.id)}
              disabled={isDisabled}
              aria-current={step.id === activeStepId ? 'step' : undefined}
              aria-label={step.label || `Step ${index + 1}`}
            >
              {renderStepContent(step, index)}
            </button>
            {index < steps.length - 1 && (
              <div className={classes.connector} aria-hidden="true" />
            )}
          </React.Fragment>
        );
      })}
    </QuickScheduleStepperStylesRoot>
  );
};

QuickScheduleStepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string
    })
  ).isRequired,
  activeStepId: PropTypes.string.isRequired,
  completedStepIds: PropTypes.arrayOf(PropTypes.string),
  onStepChange: PropTypes.func
};

QuickScheduleStepper.defaultProps = {
  completedStepIds: [],
  onStepChange: null
};

export default QuickScheduleStepper;
